/*!
=========================================================
* Muse Ant Design Dashboard - v1.0.0
=========================================================
* Product Page: https://www.creative-tim.com/product/muse-ant-design-dashboard
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/muse-ant-design-dashboard/blob/main/LICENSE.md)
* Coded by Creative Tim
=========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Radio,
  Table,
  Upload,
  message,
  Progress,
  Button,
  Avatar,
  Typography,
  Dropdown,
  Input,
  Modal,
  Spin,
  Tag,
} from "antd";

import {
  ToTopOutlined,
  SearchOutlined,
  ZoomInOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Link, useNavigate } from "react-router-dom";

// Images par défaut
import face from "../assets/images/face-1.jpg";
import pencil from "../assets/images/pencil.svg";

// ─── Utilitaire d'export ─────────────────────────────────────────────────────
const exportData = (rows, filename, format) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Export");

  if (format === "csv") {
    const csv = XLSX.utils.sheet_to_csv(ws, { FS: ";" });
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    saveAs(blob, `${filename}_${new Date().toISOString().slice(0, 10)}.csv`);
  } else {
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  }
};
// ─────────────────────────────────────────────────────────────────────────────

const { Title } = Typography;

const formProps = {
  name: "file",
  action: "https://www.mocky.io/v2/5cc8019d300000980a055e76",
  headers: {
    authorization: "authorization-text",
  },
  onChange(info) {
    if (info.file.status !== "uploading") {
      console.log(info.file, info.fileList);
    }
    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
  },
};

// Colonnes du tableau - même structure que tableWrapper.tsx
const columns = [
  {
    title: "NOM COMPLET",
    dataIndex: "fullname",
    key: "fullname",
    width: "15%",
  },
  {
    title: "EMAIL",
    dataIndex: "email",
    key: "email",
    width: "15%",
  },
  {
    title: "TÉLÉPHONE",
    dataIndex: "phone",
    key: "phone",
    width: "15%",
  },
  /*
  {
    title: "TOTAL ORGANISATIONS",
    dataIndex: "total_organizations",
    key: "total_organizations",
    width: "15%",
  }, */
  {
    title: "STATUS",
    dataIndex: "status",
    key: "status",
    width: "25%",
  },
  {
    title: "DATE DE CRÉATION",
    dataIndex: "createdAt",
    key: "createdAt",
    width: "20%",
  },
  {
    title: "ACTIONS",
    key: "actions",
    dataIndex: "actions",
  },
];

function Users() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [userDetails, setUserDetails] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 15;
  const navigate = useNavigate();

  // Nouveaux états pour les actions
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Récupération des utilisateurs depuis l'API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/users`);

        if (!res.ok) {
          throw new Error("Erreur lors de la récupération des données");
        }

        const data = await res.json();

        // Transformation des données pour correspondre au format du tableau
        const transformedData = data.map((user, index) => ({
          key: user.userUuid || index.toString(),
          userUuid: user.userUuid,
          fullname: user.fullname || "N/A",
          email: user.email || "N/A",
          phone: `+${user.phone}` || "N/A",
          //total_organizations: user.meta?.total_organizations || 0,
          status: (
            <Tag
              color={user.status === "active" ? "green" : "red"}
              // variant="solid"
              bordered={false}
            >
              {user.status}
            </Tag>
          ),
          createdAt: new Date(user.createdAt).toUTCString() || "N/A",
          actions: (
            <Dropdown
              menu={{
                items: [
                  {
                    key: "details",
                    label: (
                      <span
                        onClick={() =>
                          //fetchUserDetails(user.userUuid)
                          navigate(`/users/${user.userUuid}`)
                        }
                      >
                        Voir les détails
                      </span>
                    ),
                  },
                  {
                    key: "toggleStatus",
                    label: (
                      <span
                        onClick={async () => {
                          setActionLoading(true);
                          try {
                            const res = await fetch(
                              `${process.env.REACT_APP_API_URL}/api/users/${user.userUuid}/status`,
                              {
                                method: "PUT",
                                headers: {
                                  "Content-Type": "application/json",
                                },
                                body: JSON.stringify({
                                  status:
                                    user.status === "active"
                                      ? "inactive"
                                      : "active",
                                }),
                              },
                            );

                            if (!res.ok) {
                              throw new Error(
                                "Erreur lors du changement de statut",
                              );
                            }

                            message.success(
                              `Utilisateur ${
                                user.status === "active"
                                  ? "désactivé"
                                  : "activé"
                              } avec succès`,
                            );

                            // Rafraîchir la liste si nécessaire
                            fetchUsers();
                          } catch (err) {
                            message.error(err.message);
                          } finally {
                            setActionLoading(false);
                          }
                        }}
                      >
                        {user.status === "active"
                          ? "Désactiver le compte"
                          : "Activer le compte"}
                      </span>
                    ),
                  },
                  {
                    key: "pin",
                    label: (
                      <span
                        onClick={async () => {
                          setActionLoading(true);
                          try {
                            const res = await fetch(
                              `${process.env.REACT_APP_API_URL}/api/users/${user.userUuid}/reset-password`,
                              {
                                method: "PUT",
                                headers: {
                                  "Content-Type": "application/json",
                                },
                              },
                            );

                            if (!res.ok) {
                              throw new Error(
                                "Echec de la rénitialisation du code PIN",
                              );
                            }

                            message.success(
                              `Code PIN rénitianilisé avec succès`,
                            );

                            // Rafraîchir la liste si nécessaire
                            fetchUsers();
                          } catch (err) {
                            message.error(err.message);
                          } finally {
                            setActionLoading(false);
                          }
                        }}
                      >
                        Réinitialiser le code pin
                      </span>
                    ),
                  },
                  {
                    key: "deleteUser",
                    label: (
                      <span
                        onClick={async () => {
                          setActionLoading(true);
                          try {
                            const res = await fetch(
                              `${process.env.REACT_APP_API_URL}/api/users/${user.userUuid}/delete-user-with-org`,
                              {
                                method: "DELETE",
                                headers: {
                                  "Content-Type": "application/json",
                                },
                              },
                            );

                            if (!res.ok) {
                              throw new Error("Erreur lors de la suppression");
                            }

                            message.success(
                              `Utilisateur ${user.fullname}supprimé avec succès`,
                            );

                            // Rafraîchir la liste si nécessaire
                            fetchUsers();
                          } catch (err) {
                            message.error(err.message);
                          } finally {
                            setActionLoading(false);
                          }
                        }}
                      >
                        Supprimer le compte
                      </span>
                    ),
                  },
                ],
              }}
              placement="bottomRight"
            >
              <Button loading={actionLoading}>Actions</Button>
            </Dropdown>
          ),

          // Données brutes pour le filtrage
          rawData: {
            fullname: user.fullname,
            email: user.email,
            phone: `+${user.phone}`,
            countryCode: user.countryCode,
            createdAt: new Date(user.createdAt).toUTCString(),
            total_organizations: user.meta?.total_organizations || 0,
            status: user.status,
          },
        }));

        setUsers(transformedData);
        setFilteredUsers(transformedData);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des utilisateurs:",
          error,
        );
        message.error("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Fonction pour récupérer les organisations d'un utilisateur
  const fetchUserOrganisations = async (userUuid) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/${userUuid}/organisations`,
      );

      if (!res.ok) {
        throw new Error("Organisations introuvables");
      }

      return await res.json(); // Retourne le tableau des organisations
    } catch (error) {
      console.error("Erreur récupération organisations :", error.message);
      return [];
    }
  };

  // Fonction pour récupérer les détails d'un utilisateur
  const fetchUserDetails = async (userUuid) => {
    try {
      setDetailsLoading(true);

      // Lancer les deux requêtes en parallèle
      const [userRes, orgs] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/users/${userUuid}`),
        fetchUserOrganisations(userUuid),
      ]);

      if (!userRes.ok) {
        throw new Error("Utilisateur introuvable");
      }

      const userData = await userRes.json();

      // Ajouter les organisations aux détails utilisateur
      setUserDetails({
        ...userData,
        organisations: orgs,
      });

      setModalVisible(true);
    } catch (error) {
      console.error("Erreur :", error.message);
      message.error("Erreur lors du chargement des détails");
      setUserDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Filtrage des utilisateurs avec pagination manuelle
  useEffect(() => {
    let filtered = users;

    // Filtrage par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter((user) => {
        const searchableText = `${user.rawData.fullname} ${user.rawData.email} ${user.rawData.phone}`;
        return searchableText.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filtrage par statut (pour l'instant tous sont "online")
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (user) => user.rawData.status === statusFilter,
      );
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, statusFilter]);

  // Pagination manuelle
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const onChange = (e) => {
    setStatusFilter(e.target.value);
    console.log(`radio checked:${e.target.value}`);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleExport = (format) => {
    const rows = filteredUsers.map((u) => ({
      "Nom complet": u.rawData.fullname,
      Email: u.rawData.email,
      Téléphone: u.rawData.phone,
      Statut: u.rawData.status,
      "Date de création": u.rawData.createdAt,
    }));
    exportData(rows, "utilisateurs", format);
  };

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs="24" xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="Tous les clients"
              extra={
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      alignItems: "center",
                    }}
                  >
                    <Input
                      placeholder="Rechercher des utilisateurs"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: 250 }}
                      prefix={<SearchOutlined />}
                    />
                    <Radio.Group onChange={onChange} defaultValue="all">
                      <Radio.Button value="all">Tous</Radio.Button>
                      <Radio.Button value="active">Actifs</Radio.Button>
                      <Radio.Button value="inactive">Inactifs</Radio.Button>
                    </Radio.Group>
                    <Dropdown
                      menu={{
                        items: [
                          {
                            key: "csv",
                            label: "Exporter en CSV",
                            icon: <DownloadOutlined />,
                            onClick: () => handleExport("csv"),
                          },
                          {
                            key: "xlsx",
                            label: "Exporter en Excel (.xlsx)",
                            icon: <DownloadOutlined />,
                            onClick: () => handleExport("xlsx"),
                          },
                        ],
                      }}
                      placement="bottomRight"
                    >
                      <Button icon={<DownloadOutlined />}>Exporter</Button>
                    </Dropdown>
                  </div>
                </>
              }
            >
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={currentUsers}
                  loading={loading}
                  pagination={false} // Désactivation de la pagination automatique
                  className="ant-border-space font-semibold"
                />
              </div>

              {/* Pagination manuelle comme dans tableWrapper.tsx */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "16px",
                }}
              >
                <Button onClick={handlePrevPage} disabled={currentPage === 1}>
                  Précédent
                </Button>
                <span>
                  Page {currentPage} sur {totalPages} ({filteredUsers.length}{" "}
                  utilisateurs)
                </span>
                <Button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  Suivant
                </Button>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Modal pour afficher les détails de l'utilisateur */}
        <Modal
          title="Détails de l'utilisateur"
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setModalVisible(false)}>
              Fermer
            </Button>,
          ]}
          width={600}
        >
          {detailsLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Spin size="large" />
            </div>
          ) : userDetails ? (
            <div>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <p>
                    <strong>Nom complet :</strong>{" "}
                    {userDetails.fullname || "N/A"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Email :</strong> {userDetails.email || "N/A"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Téléphone :</strong> +{userDetails.phone || "N/A"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Code du pays :</strong>{" "}
                    {userDetails.countryCode || "N/A"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Nombre d'organisations :</strong>{" "}
                    {userDetails.organisations.length || 0}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Date de création :</strong>{" "}
                    {userDetails.createdAt
                      ? new Date(userDetails.createdAt).toLocaleDateString()
                      : "N/A"}
                  </p>
                </Col>
                <Col span={12}>
                  <p>
                    <strong>Date de mise à jour :</strong>{" "}
                    {userDetails.updatedAt
                      ? new Date(userDetails.updatedAt).toLocaleDateString()
                      : "N/A"}
                  </p>
                </Col>
              </Row>
              <div
                className="text-lg"
                style={{
                  marginBottom: "15px",
                  marginTop: "15px",
                }}
              >
                Organisations
              </div>
              <Row gutter={[16, 16]}>
                {userDetails.organisations &&
                userDetails.organisations.length > 0 ? (
                  userDetails.organisations.map((org) => (
                    <Card
                      key={org.organisationId}
                      style={{
                        marginBottom: "10px",
                        marginRight: "20px",
                        background: "#fafafa",
                      }}
                    >
                      <p>
                        <strong>Nom:</strong> {org.name}
                      </p>
                      <p>
                        <strong>Téléphone :</strong> +{org.phone}
                      </p>
                      <p>
                        <strong>Statut :</strong> {org.status}
                      </p>
                      <p>
                        <strong>Type :</strong>{" "}
                        {org.organisationType === "main"
                          ? "principale"
                          : "secondaire"}
                      </p>
                      <p>
                        <strong>Créé le :</strong>{" "}
                        {new Date(org.createdAt).toLocaleString()}
                      </p>
                    </Card>
                  ))
                ) : (
                  <p>Aucune organisation trouvée.</p>
                )}
              </Row>
            </div>
          ) : (
            <p>Erreur lors du chargement des détails</p>
          )}
        </Modal>
      </div>
    </>
  );
}

export default Users;
